/**
 * Transport: POST to act, SSE to listen. Nothing here knows the rules — the
 * server is authoritative and this module only carries messages.
 */

import type { ActionResponse, GameAction, PlayerView } from '@manille/core';

import { deviceToken } from './device.js';

export type ConnectionStatus = 'connecting' | 'live' | 'lost';

async function postJson(url: string, body: unknown): Promise<Response> {
  return fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function createGame(name: string): Promise<string> {
  const response = await postJson('/api/games', { playerId: deviceToken(), name });
  if (!response.ok) throw new Error('Kon geen tafel maken.');
  const body = (await response.json()) as { gameId: string };
  return body.gameId;
}

export async function joinGame(gameId: string, name: string): Promise<ActionResponse> {
  const response = await postJson(`/api/games/${gameId}/join`, {
    playerId: deviceToken(),
    name,
  });
  return (await response.json()) as ActionResponse;
}

export async function sendAction(gameId: string, action: GameAction): Promise<ActionResponse> {
  try {
    const response = await postJson(`/api/games/${gameId}/action`, {
      playerId: deviceToken(),
      action,
    });
    return (await response.json()) as ActionResponse;
  } catch {
    return { ok: false, code: 'unknown-action', message: 'Verbinding verbroken.' };
  }
}

export async function gameExists(gameId: string): Promise<boolean> {
  try {
    const response = await fetch(`/api/games/${gameId}`);
    if (!response.ok) return false;
    const body = (await response.json()) as { exists: boolean };
    return body.exists;
  } catch {
    return false;
  }
}

/**
 * Subscribe to the table. `EventSource` reconnects on its own; we surface the
 * gap so the UI can say so rather than silently showing a stale table.
 */
export function connect(
  gameId: string,
  onView: (view: PlayerView) => void,
  onStatus: (status: ConnectionStatus) => void,
): () => void {
  const url = `/api/games/${gameId}/events?playerId=${encodeURIComponent(deviceToken())}`;
  const source = new EventSource(url);
  onStatus('connecting');

  source.addEventListener('open', () => onStatus('live'));
  source.addEventListener('error', () => {
    onStatus(source.readyState === EventSource.CLOSED ? 'lost' : 'connecting');
  });
  source.addEventListener('view', (event) => {
    try {
      const payload = JSON.parse((event as MessageEvent<string>).data) as { view: PlayerView };
      onStatus('live');
      onView(payload.view);
    } catch {
      /* a malformed frame is not worth tearing the stream down for */
    }
  });

  return () => source.close();
}
